import {reactFetch} from '@github-ui/verified-fetch'
import {Checkbox, Flash, FormControl, Portal, Textarea} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {useCallback, useState} from 'react'

import styles from './FeedbackDialog.module.css'

const FEEDBACK_PATH = '/search/feedback'

/**
 * React port of the search feedback dialog that historically lived on the legacy
 * `<qbsearch-input>` element. Controlled by `SearchSubHeader` (rendered only
 * while open, like `ExperimentsDialog`), which owns the open state and opens it
 * in response to the shared `PROVIDE_FEEDBACK_EVENT`. Submits to
 * `/search/feedback` via `reactFetch`.
 */
export function FeedbackDialog({onClose}: {onClose: () => void}) {
  const [feedback, setFeedback] = useState('')
  const [includeEmail, setIncludeEmail] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | undefined>(undefined)

  const submit = useCallback(async () => {
    if (feedback.trim() === '' || isSubmitting) return
    setIsSubmitting(true)
    setSubmitError(undefined)
    const body = new FormData()
    body.append('feedback', feedback)
    if (includeEmail) body.append('include_email', 'on')
    try {
      const res = await reactFetch(FEEDBACK_PATH, {method: 'POST', body})
      if (!res.ok) {
        setSubmitError('Could not submit your feedback. Please try again.')
        setIsSubmitting(false)
        return
      }
      onClose()
    } catch {
      setSubmitError('Could not submit your feedback. Please check your connection and try again.')
    }
    setIsSubmitting(false)
  }, [feedback, includeEmail, isSubmitting, onClose])

  return (
    <Portal>
      <Dialog onClose={onClose} title="Provide feedback" width="large">
        <Dialog.Body>
          <div className={styles.body}>
            <p className={styles.intro}>We read every piece of feedback, and take your input very seriously.</p>
            {submitError && (
              <Flash variant="danger" role="alert">
                {submitError}
              </Flash>
            )}
            <FormControl required>
              <FormControl.Label>Feedback</FormControl.Label>
              <Textarea
                className={styles.textarea}
                value={feedback}
                onChange={e => setFeedback(e.target.value)}
                resize="vertical"
              />
            </FormControl>
            <FormControl>
              <Checkbox checked={includeEmail} onChange={e => setIncludeEmail(e.target.checked)} />
              <FormControl.Label>Include my email address so I can be contacted</FormControl.Label>
            </FormControl>
          </div>
        </Dialog.Body>
        <Dialog.Footer>
          <Dialog.Buttons
            buttons={[
              {type: 'button', onClick: onClose, content: 'Cancel'},
              {
                type: 'button',
                onClick: () => void submit(),
                content: 'Send feedback',
                buttonType: 'primary',
                disabled: feedback.trim() === '',
                loading: isSubmitting,
                loadingAnnouncement: 'Sending feedback',
              },
            ]}
          />
        </Dialog.Footer>
      </Dialog>
    </Portal>
  )
}
